import { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../ui/card";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { InputFieldset } from 'ui/inputFieldset';
import { Label } from "../../ui/label";
import { Badge } from "../../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import {
  // Plus,
  Edit2,
  Users,
  Search,
  // Calendar,
  // Info,
  Check,
  X,
  FileText,
  Hotel,
  Car,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Filter,
  BarChart3,
} from "lucide-react";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Switch } from "../../ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../ui/tabs";
import {
  AgeRuleApi,
  createAgeRule,
  getAgeRules,
  updateAgeRule,
} from "../../services/ageRuleService";
import { ApiModule, getActiveModules } from "../../services/moduleService";
import { useScreenPermission } from "../../hooks/useScreenPermission";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";
import Pagination from "ui/pagination";

interface AgeRule {
  id: string;
  categoryName: string;
  minAge: number;
  maxAge: number;
  module: "visa" | "accommodation" | "transport";
  calculationMethod: string;
  effectiveFromDate: string;
  effectiveToDate: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

// Age Calculation Methods
const calculationMethods = [
  {
    value: "arrival_date",
    label: "Age on Arrival Date",
    description: "Age calculated based on group arrival date",
  },
  {
    value: "departure_date",
    label: "Age on Departure Date",
    description: "Age calculated based on group departure date",
  },
  {
    value: "application_date",
    label: "Age on Application Date",
    description: "Age calculated based on visa application submission date",
  },
  {
    value: "event_date",
    label: "Age on Event Date",
    description: "Age calculated based on specific event date",
  },
];

export const AgeRuleMasterPage = () => {
  const { setPageHeader } = usePageHeader();
  const [ageRules, setAgeRules] = useState<AgeRule[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDialog, setShowDialog] = useState(false);
  const [editingRule, setEditingRule] = useState<AgeRule | null>(null);
  const [activeModule, setActiveModule] = useState<
    "visa" | "accommodation" | "transport"
  >("visa");
  const [showStatistics, setShowStatistics] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Sorting state
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const [loading, setLoading] = useState(true);
  const [modules, setModules] = useState<ApiModule[]>([]);

 const [pageNo, setPageNo] = useState(1);
 const [pageSize, setPageSize] = useState(10);
 const [totalCount, setTotalCount] = useState(0); 

  const { canEdit, screenName, icon: ScreenIcon } = useScreenPermission();

  // Filter states
  const [filters, setFilters] = useState({
    categoryName: "",
    module: "all",
    calculationMethod: "all",
    status: "all",
    minAge: "",
    maxAge: "",
  });

  const [formData, setFormData] = useState({
    categoryName: "",
    minAge: "",
    maxAge: "",
    module: "",
    calculationMethod: "",
    effectiveFromDate: new Date().toISOString().split("T")[0],
    effectiveToDate:  new Date().toISOString().split("T")[0],
    isActive: true,
  });

  useEffect(() => {
    setPageHeader({
      title: screenName,
      icon: <ScreenIcon className="h-6 w-6" />,
    });

    return () => setPageHeader(null);
  }, [screenName, ScreenIcon, setPageHeader]);

  useEffect(() => {
  const loadModules = async () => {
    try {
      const res = await getActiveModules();
      setModules(res);
    } catch {
      toast.error("Failed to load modules");
    }
  };

  loadModules();
}, []);

  const moduleIdToCodeMap = useMemo(() => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const map: Record<number, "visa" | "accommodation" | "transport"> = {} as any;

    modules.forEach((m) => {
      switch (m.moduleCode.toUpperCase()) {
        case "VISA":
          map[m.moduleId] = "visa";
          break;

        case "ACOM":
          map[m.moduleId] = "accommodation";
          break;

        case "TRPT":
          map[m.moduleId] = "transport";
          break;
      }
    });

    return map;
  }, [modules]);

  const moduleCodeToIdMap = useMemo(() => {
    const map: Record<string, number> = {};

    modules.forEach((m) => {
      switch (m.moduleCode.toUpperCase()) {
        case "VISA":
          map["visa"] = m.moduleId;
          break;

        case "ACOM":
          map["accommodation"] = m.moduleId;
          break;

        case "TRPT":
          map["transport"] = m.moduleId;
          break;
      }
    });

    return map;
  }, [modules]);

  const getModuleIdByCode = (code: string) => moduleCodeToIdMap[code];

  const mapApiToUI = (api: AgeRuleApi): AgeRule => ({
    id: String(api.ageRuleId),
    categoryName: api.categoryName,
    minAge: api.minAge,
    maxAge: api.maxAge,
    module: moduleIdToCodeMap[api.moduleId] ?? "visa",
    calculationMethod: api.calculationMethod,
    effectiveFromDate: api.effectiveFrom.split("T")[0],
    effectiveToDate: api.effectiveTo
      ? api.effectiveTo.split("T")[0]
      : "",
    isActive: api.isActive,
    createdAt: api.createdAt.split("T")[0],
    updatedAt: api.updatedAt ? api.updatedAt.split("T")[0] : "",
  });

 useEffect(() => {
  if (modules.length === 0) return;
  loadAgeRules();
// eslint-disable-next-line react-hooks/exhaustive-deps
}, [modules, searchTerm, filters, activeModule, pageNo, pageSize]);

  const loadAgeRules = async () => {
    setLoading(true);
    try {
      const res = await getAgeRules({
        searchText: searchTerm || null,
        categoryName: filters.categoryName || null,
        moduleId: getModuleIdByCode(activeModule),
        calculationMethod:
          filters.calculationMethod === "all"
            ? null
            : filters.calculationMethod,
        fromAge: filters.minAge ? Number(filters.minAge) : null,
        toAge: filters.maxAge ? Number(filters.maxAge) : null,
        isActive: filters.status === "all" ? null : filters.status === "active",
        pageNo,
        pageSize,
      });

      setAgeRules(res.items.map(mapApiToUI));
      setTotalCount(res.totalCount);
    } catch {
      toast.error("Failed to load age rules");
    } finally {
    setLoading(false);
  }
  };

  const handleEdit = (rule: AgeRule) => {
    setEditingRule(rule);
    setFormData({
      categoryName: rule.categoryName,
      minAge: rule.minAge.toString(),
      maxAge: rule.maxAge.toString(),
      module: rule.module,
      calculationMethod: rule.calculationMethod,
      effectiveFromDate: rule.effectiveFromDate,
      effectiveToDate: rule.effectiveToDate,
      isActive: rule.isActive,
    });
    setShowDialog(true);
  };

  const isDuplicateCategoryInModule = () => {
    const normalizedName = formData.categoryName.trim().toLowerCase();
    return ageRules.some((rule) => {
      const sameCategory =
        rule.categoryName.trim().toLowerCase() === normalizedName;

      const sameModule = rule.module === formData.module;
      const notSameRecord = editingRule ? rule.id !== editingRule.id : true;

      return sameCategory && sameModule && notSameRecord;
    });
  };

  const handleSave = async () => {
    try {

      if (
        !formData.categoryName ||
        !formData.minAge ||
        !formData.maxAge ||
        !formData.module ||
        !formData.calculationMethod
      ) {
        toast.error("Please fill all required fields");
        return;
      }

      const categoryName = formData.categoryName.trim();

      if (!/^[A-Za-z\s]+$/.test(categoryName)) {
        toast.error("Category Name can contain only letters");
        return;
      }

      if (categoryName.length > 15) {
        toast.error("Category Name cannot exceed 15 characters");
        return;
      }

      if (isDuplicateCategoryInModule()) {
        toast.error(
          `Category "${formData.categoryName}" already exists in ${formData.module}`
        );
        return;
      }

      const moduleId = getModuleIdByCode(formData.module);
      if (!moduleId) {
        toast.error("Invalid module selected");
        return;
      }

      if (editingRule) {
        await updateAgeRule({
          ageRuleId: Number(editingRule.id),
          categoryName: formData.categoryName.trim(),
          minAge: Number(formData.minAge),
          maxAge: Number(formData.maxAge),
          moduleId,
          calculationMethod: formData.calculationMethod,
          effectiveFrom: formData.effectiveFromDate,
          effectiveTo: formData.effectiveToDate || "9999-12-31",
          isActive: formData.isActive,
          updatedBy: 1, 
        });

        toast.success("Age rule updated successfully");
      } else {
        await createAgeRule({
          categoryName: formData.categoryName.trim(),
          minAge: Number(formData.minAge),
          maxAge: Number(formData.maxAge),
          moduleId,
          calculationMethod: formData.calculationMethod,
          effectiveFrom: formData.effectiveFromDate,
          effectiveTo: formData.effectiveToDate || "9999-12-31",
          isActive: formData.isActive,
          createdBy: 1, 
        });

        toast.success("Age rule added successfully");
      }

      setPageNo(1);
      await loadAgeRules();
      handleCloseDialog();
    } catch (error) {
      console.error("Save age rule error:", error);
      toast.error("Failed to save age rule");
    }
  };

  const handleCloseDialog = () => {
    setShowDialog(false);
    setEditingRule(null);
    setFormData({
      categoryName: "",
      minAge: "",
      maxAge: "",
      module: "",
      calculationMethod: "",
      effectiveFromDate: new Date().toISOString().split("T")[0],
      effectiveToDate: new Date().toISOString().split("T")[0],
      isActive: true,
    });
  };

  // Filter rules
  const filteredRules = useMemo(() => {
    return ageRules.filter((rule) => {
      const matchesSearch =
        rule.categoryName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rule.module.toLowerCase().includes(searchTerm.toLowerCase()) ||
        rule.calculationMethod.toLowerCase().includes(searchTerm.toLowerCase());

      const matchesModule = rule.module === activeModule;
      const matchesCategoryName =
        !filters.categoryName ||
        rule.categoryName
          .toLowerCase()
          .includes(filters.categoryName.toLowerCase());

      const matchesFilterModule =
        filters.module === "all" || rule.module === filters.module;

      const matchesCalculationMethod =
        filters.calculationMethod === "all" ||
        rule.calculationMethod === filters.calculationMethod;

      const matchesStatus =
        filters.status === "all" ||
        (filters.status === "active" ? rule.isActive : !rule.isActive);

      const matchesMinAge =
        !filters.minAge || rule.minAge >= Number(filters.minAge);

      const matchesMaxAge =
        !filters.maxAge || rule.maxAge <= Number(filters.maxAge);

      return (
        matchesSearch &&
        matchesModule &&
        matchesCategoryName &&
        matchesFilterModule &&
        matchesCalculationMethod &&
        matchesStatus &&
        matchesMinAge &&
        matchesMaxAge
      );
    });
  }, [ageRules, searchTerm, activeModule, filters]);

  // Sort rules
  const sortedRules = [...filteredRules].sort((a, b) => {
    if (!sortColumn) return 0;

    // Extract unknown values safely
    const aRaw = a[sortColumn as keyof AgeRule];
    const bRaw = b[sortColumn as keyof AgeRule];

    // Normalize values for comparison
    const normalize = (val: unknown): string | number => {
      if (typeof val === "string") return val.toLowerCase();
      if (typeof val === "number") return val;
      if (typeof val === "boolean") return val ? 1 : 0;
      return 0;
    };

    const aValue = normalize(aRaw);
    const bValue = normalize(bRaw);

    if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
    if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const SortableHeader = ({
    column,
    label,
  }: {
    column: string;
    label: string;
  }) => (
    <TableHead
      style={{ color: "#5B9BD5" }}
      className="px-4 py-3 text-xs cursor-pointer hover:bg-[#5B9BD5]/10 transition-colors select-none"
      onClick={() => handleSort(column)}
    >
      <div className="flex items-center gap-2">
        <span>{label}</span>
        {sortColumn === column ? (
          sortDirection === "asc" ? (
            <ArrowUp className="w-3 h-3" />
          ) : (
            <ArrowDown className="w-3 h-3" />
          )
        ) : (
          <ArrowUpDown className="w-3 h-3 opacity-40" />
        )}
      </div>
    </TableHead>
  );

  // Get module icon
  const getModuleIcon = (module: string) => {
    switch (module) {
      case "visa":
        return <FileText className="w-4 h-4" />;
      case "accommodation":
        return <Hotel className="w-4 h-4" />;
      case "transport":
        return <Car className="w-4 h-4" />;
      default:
        return null;
    }
  };

  // Get module color - all blue now
  const getModuleColor = (module: string) => {
    return "bg-blue-100 text-blue-700 border-blue-300";
  };

  // Group rules by module for display
  const visaRules = sortedRules.filter((r) => r.module === "visa");
  const accommodationRules = sortedRules.filter(
    (r) => r.module === "accommodation"
  );
  const transportRules = sortedRules.filter((r) => r.module === "transport");

  // Get calculation method label
  const getCalculationMethodLabel = (method: string) => {
    return calculationMethods.find((m) => m.value === method)?.label || method;
  };

  // Check if any filters are active
  const hasActiveFilters =
    filters.categoryName !== "" ||
    filters.module !== "all" ||
    filters.calculationMethod !== "all" ||
    filters.status !== "all" ||
    filters.minAge !== "" ||
    filters.maxAge !== "";

  return (
    <>
      {loading && <FullScreenLoading />}
      <div className="space-y-4 sm:space-y-6 pb-8 px-4 sm:px-0">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center w-full gap-2">
          <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap w-full">
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />

              <Input
                placeholder="Search by category name..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setPageNo(1);
                }}
                className="h-10 pl-9 pr-9 shadow-sm"
              />

              {searchTerm && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchTerm("");
                    setPageNo(1);
                  }}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              variant="outline"
              onClick={() => setShowStatistics(!showStatistics)}
              className="border-[#5B9BD5]/30 text-[#5B9BD5] hover:bg-[#5B9BD5]/10 shrink-0"
            >
              {showStatistics ? (
                <ArrowUp className="w-5 h-5" />
              ) : (
                <BarChart3 className="w-5 h-5" />
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`shrink-0 ${
                showFilters
                  ? "text-white"
                  : "border-[#5B9BD5] text-[#5B9BD5] hover:bg-sky-50"
              }`}
              title="Advanced Filters"
            >
              <Filter className="w-4 h-4 sm:mr-0" />
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        {showStatistics && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <Card className="border-green-200 bg-green-50/50">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-green-600">
                      Active Rules
                    </p>
                    <p className="text-xl sm:text-2xl font-bold text-green-900">
                      {ageRules.filter((r) => r.isActive).length}
                    </p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <Check className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-blue-200 bg-blue-50/50">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-blue-600">Visa Rules</p>
                    <p className="text-xl sm:text-2xl font-bold text-blue-900">
                      {ageRules.filter((r) => r.module === "visa").length}
                    </p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-blue-100 flex items-center justify-center">
                    <FileText className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-purple-200 bg-purple-50/50">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-purple-600">
                      Accommodation Rules
                    </p>
                    <p className="text-xl sm:text-2xl font-bold text-purple-900">
                      {
                        ageRules.filter((r) => r.module === "accommodation")
                          .length
                      }
                    </p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <Hotel className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-3 sm:p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-xs sm:text-sm text-amber-600">
                      Transport Rules
                    </p>
                    <p className="text-xl sm:text-2xl font-bold text-amber-900">
                      {ageRules.filter((r) => r.module === "transport").length}
                    </p>
                  </div>
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-amber-100 flex items-center justify-center">
                    <Car className="w-5 h-5 sm:w-6 sm:h-6 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Advanced Filters */}
        {showFilters && (
          <Card className="bg-white border-[#5B9BD5]/30" style={{ gap: "0px" }}>
            <CardHeader className="pb-3 px-3 sm:px-6 pt-3 sm:pt-6">
              <div className="flex items-center justify-between">
                <CardTitle
                  style={{ color: "#5B9BD5" }}
                  className="text-sm sm:text-base"
                >
                  Advanced Filters
                </CardTitle>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowFilters(false)}
                  className="text-gray-500 hover:text-gray-700 h-8 w-8 p-0"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3 sm:space-y-4 px-3 sm:px-6 pb-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                {/* Category Name Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-category" className="text-sm">
                    Category Name
                  </Label>
                  <Input
                    id="filter-category"
                    placeholder="Search by category..."
                    value={filters.categoryName}
                    onChange={(e) => {
                      setFilters({
                        ...filters,
                        categoryName: e.target.value,
                      });
                      setPageNo(1);
                    }}
                    className="h-9 text-sm"
                  />
                </div>

                {/* Module Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-module" className="text-sm">
                    Module
                  </Label>
                  <Select
                    value={filters.module}
                    onValueChange={(value) => {
                      setFilters({
                        ...filters,
                        module: value,
                      });
                      setPageNo(1);
                    }}
                  >
                    <SelectTrigger id="filter-module" className="h-9 text-sm">
                      <SelectValue placeholder="All modules" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Modules</SelectItem>
                      {modules.map((m) => (
    <SelectItem key={m.moduleId} value={m.moduleCode.toLowerCase()}>
      {m.moduleName}
    </SelectItem>
  ))}

                    </SelectContent>
                  </Select>
                </div>

                {/* Calculation Method Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-calculation" className="text-sm">
                    Calculation Method
                  </Label>
                  <Select
                    value={filters.calculationMethod}
                    onValueChange={(value) => {
                      setFilters({ ...filters, calculationMethod: value });
                      setPageNo(1);
                    }}
                  >
                    <SelectTrigger
                      id="filter-calculation"
                      className="h-9 text-sm"
                    >
                      <SelectValue placeholder="All methods" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Methods</SelectItem>
                      {calculationMethods.map((method) => (
                        <SelectItem key={method.value} value={method.value}>
                          {method.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Status Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-status" className="text-sm">
                    Status
                  </Label>
                  <Select
                    value={filters.status}
                    onValueChange={(value) => {
                      setFilters({ ...filters, status: value });
                      setPageNo(1);
                    }}
                  >
                    <SelectTrigger id="filter-status" className="h-9 text-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Status</SelectItem>
                      <SelectItem value="active">Active Only</SelectItem>
                      <SelectItem value="inactive">Inactive Only</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Min Age Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-min-age" className="text-sm">
                    Minimum Age (From)
                  </Label>
                  <Input
                    id="filter-min-age"
                    type="number"
                    min="0"
                    placeholder="e.g., 0"
                    value={filters.minAge}
                    onChange={(e) => {
                      setFilters({ ...filters, minAge: e.target.value });
                      setPageNo(1);
                    }}
                    className="h-9 text-sm"
                  />
                </div>

                {/* Max Age Filter */}
                <div className="space-y-2">
                  <Label htmlFor="filter-max-age" className="text-sm">
                    Maximum Age (To)
                  </Label>
                  <Input
                    id="filter-max-age"
                    type="number"
                    min="0"
                    placeholder="e.g., 120"
                    value={filters.maxAge}
                    onChange={(e) => {
                      setFilters({ ...filters, maxAge: e.target.value })
                      setPageNo(1);
                    }
                    }
                    className="h-9 text-sm"
                  />
                </div>
              </div>

              {/* Active Filters Display */}
              {hasActiveFilters && (
                <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 pt-3 border-t">
                  <span className="text-xs sm:text-sm text-gray-600 font-medium">
                    Active Filters:
                  </span>
                  <div className="flex flex-wrap gap-2 flex-1">
                    {filters.categoryName && (
                      <Badge
                        variant="outline"
                        className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] sm:text-xs"
                      >
                        Category: {filters.categoryName}
                      </Badge>
                    )}
                    {filters.module !== "all" && (
                      <Badge
                        variant="outline"
                        className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] sm:text-xs"
                      >
                        Module: {filters.module}
                      </Badge>
                    )}
                    {filters.calculationMethod !== "all" && (
                      <Badge
                        variant="outline"
                        className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] sm:text-xs"
                      >
                        Method:{" "}
                        {getCalculationMethodLabel(filters.calculationMethod)}
                      </Badge>
                    )}
                    {filters.status !== "all" && (
                      <Badge
                        variant="outline"
                        className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] sm:text-xs"
                      >
                        Status: {filters.status}
                      </Badge>
                    )}
                    {filters.minAge && (
                      <Badge
                        variant="outline"
                        className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] sm:text-xs"
                      >
                        Min Age: {filters.minAge}
                      </Badge>
                    )}
                    {filters.maxAge && (
                      <Badge
                        variant="outline"
                        className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] sm:text-xs"
                      >
                        Max Age: {filters.maxAge}
                      </Badge>
                    )}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setFilters({
                        categoryName: "",
                        module: "all",
                        calculationMethod: "all",
                        status: "all",
                        minAge: "",
                        maxAge: "",
                      });
                    }}
                    className="text-red-600 border-red-300 hover:bg-red-50 text-xs sm:text-sm w-full sm:w-auto sm:ml-auto h-8"
                  >
                    <X className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                    Clear All
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Age Rules Table - Grouped by Module */}
        <Tabs
          value={activeModule}
          onValueChange={(value: string) => {
            setActiveModule(value as "visa" | "accommodation" | "transport");
            setPageNo(1);
          }}

          
          className="space-y-4"
          style={{ gap:"0px" , marginTop: "10px" }}
        >
          <TabsList className="grid w-full grid-cols-3 bg-sky-50 border border-[#5B9BD5]/30 h-auto">
            <TabsTrigger
              value="visa"
              className="data-[state=active]:bg-[#5B9BD5] data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5"
            >
              <FileText className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline ml-1 sm:ml-0">Visa</span>
            </TabsTrigger>
            <TabsTrigger
              value="accommodation"
              className="data-[state=active]:bg-[#5B9BD5] data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5"
            >
              <Hotel className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline ml-1 sm:ml-0">Accommodation</span>
            </TabsTrigger>
            <TabsTrigger
              value="transport"
              className="data-[state=active]:bg-[#5B9BD5] data-[state=active]:text-white text-xs sm:text-sm px-2 sm:px-4 py-2 sm:py-2.5"
            >
              <Car className="w-3 h-3 sm:w-4 sm:h-4 sm:mr-2" />
              <span className="hidden sm:inline ml-1 sm:ml-0">Transport</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="visa">
            <Card
              className="bg-white border-[#5B9BD5]/30"
              style={{ gap: "0px", marginTop: "0px" }}
            >
              <CardContent style={{ padding: "0px" }}>
                {loading ? (
                  <div className="py-10 text-center text-gray-500 text-sm">
                    Loading visa age rules...
                  </div>
                ) : visaRules.length === 0 ? (
                  <div className="py-10 text-center text-gray-500 text-sm">
                    No visa age rules found
                  </div>
                ) : (
                  <>
                  <div className="hidden lg:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                          <SortableHeader
                            column="categoryName"
                            label="Category Name"
                          />
                          <SortableHeader column="minAge" label="Min Age" />
                          <SortableHeader column="maxAge" label="Max Age" />
                          {/* <SortableHeader
                            column="calculationMethod"
                            label="Calculation Method"
                          />
                          <SortableHeader
                            column="effectiveToDate"
                            label="Effective To Date"
                          />
                          <SortableHeader column="isActive" label="Status" /> */}
                          {canEdit && (
                            <TableHead
                              style={{ color: "#5B9BD5" }}
                              className="px-4 py-3 text-xs text-right"
                            >
                              Actions
                            </TableHead>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {visaRules.map((rule) => (
                          <TableRow key={rule.id} className="hover:bg-sky-50/30">
                            <TableCell className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                                  <Users className="w-4 h-4 text-[#5B9BD5]" />
                                </div>
                                <span className="text-gray-900 text-sm">
                                  {rule.categoryName}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="px-4 py-3">
                              <span className="text-xs text-gray-700">
                                {rule.minAge} years
                              </span>
                            </TableCell>
                            <TableCell className="px-4 py-3">
                              <span className="text-xs text-gray-700">
                                {rule.maxAge} years
                              </span>
                            </TableCell>
                            {/* <TableCell className="px-4 py-3">
                              <Badge
                                variant="outline"
                                className="!border-[#5B9BD5]/30 !text-[#5B9BD5] text-[10px] px-1.5 py-0.5 rounded-full"
                              >
                                {getCalculationMethodLabel(
                                  rule.calculationMethod
                                )}
                              </Badge>
                            </TableCell> */}
                            {/* <TableCell className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3 text-gray-500" />
                                <span className="text-xs text-gray-700">
                                  {rule.effectiveToDate}
                                </span>
                              </div>
                            </TableCell> */}
                            {/* <TableCell className="px-4 py-3">
                              <Badge
                                className={`
                                  border text-[10px] px-1.5 py-0.5 rounded-full
                                  ${
                                    rule.isActive
                                      ? "!bg-green-100 !text-green-700 !border-green-300"
                                      : "!bg-gray-100 !text-gray-700 !border-gray-300"
                                  }
                                `}
                              >
                                {rule.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </TableCell> */}
                            {canEdit && (
                              <TableCell className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEdit(rule)}
                                    className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                    title="Edit age rule"
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="lg:hidden divide-y divide-gray-200">
                    {visaRules.map((rule) => (
                      <div
                        key={rule.id}
                        className="p-4 hover:bg-sky-50/30 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Users className="w-4 h-4 text-[#5B9BD5]" />
                              <h3 className="font-medium text-gray-900">
                                {rule.categoryName}
                              </h3>
                            </div>
                            <Badge
                              className={getModuleColor(rule.module)}
                              style={{ fontSize: "10px", padding: "2px 6px" }}
                            >
                              {getModuleIcon(rule.module)}
                              <span className="ml-1">{rule.module}</span>
                            </Badge>
                          </div>
                          {/* <Badge
                            className={`${rule.isActive ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5`}
                          >
                            {rule.isActive ? "Active" : "Inactive"}
                          </Badge> */}
                        </div>

                        <div className="space-y-2 mb-3 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Age Range:</span>
                            <span className="font-medium text-gray-900">
                              {rule.minAge} - {rule.maxAge} years
                            </span>
                          </div>
                          {/* <div className="flex justify-between">
                            <span className="text-gray-600">Calculation:</span>
                            <span className="text-gray-900 text-xs">
                              {getCalculationMethodLabel(rule.calculationMethod)}
                            </span>
                          </div> */}
                          {/* <div className="flex justify-between">
                            <span className="text-gray-600">Effective To:</span>
                            <span className="text-gray-900">
                              {rule.effectiveToDate}
                            </span>
                          </div> */}
                        </div>

                        <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(rule)}
                            className="flex-1 text-[#5B9BD5] border-[#5B9BD5]/30 hover:bg-sky-50 h-8"
                          >
                            <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                            Edit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
    </>
        )} 
                </CardContent>
                <Pagination
                  totalItems={totalCount}
                  itemsPerPage={pageSize}
                  currentPage={pageNo}
                  onPageChange={(page) => setPageNo(page)}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPageNo(1);
                  }}
                  label="age rules"
                />
              </Card>
          

            {/* Accommodation Rules */}
            {accommodationRules.length > 0 && (
              <Card className="border-[#5B9BD5]/30">
                <CardContent className="p-0">
                  <div className="hidden lg:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                          <SortableHeader
                            column="categoryName"
                            label="Category Name"
                          />
                          <SortableHeader column="minAge" label="Min Age" />
                          <SortableHeader column="maxAge" label="Max Age" />
                          {/* <SortableHeader
                            column="calculationMethod"
                            label="Calculation Method"
                          /> */}
                          {/* <SortableHeader
                            column="effectiveToDate"
                            label="Effective To Date"
                          /> */}
                          {/* <SortableHeader column="isActive" label="Status" /> */}
                          {canEdit && (
                            <TableHead
                              style={{ color: "#5B9BD5" }}
                              className="px-4 py-3 text-xs text-right"
                            >
                              Actions
                            </TableHead>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {accommodationRules.map((rule) => (
                          <TableRow key={rule.id} className="hover:bg-sky-50/30">
                            <TableCell className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                                  <Users className="w-4 h-4 text-[#5B9BD5]" />
                                </div>
                                <span className="text-gray-900 text-sm">
                                  {rule.categoryName}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="px-4 py-3">
                              <span className="text-xs text-gray-700">
                                {rule.minAge} years
                              </span>
                            </TableCell>
                            <TableCell className="px-4 py-3">
                              <span className="text-xs text-gray-700">
                                {rule.maxAge} years
                              </span>
                            </TableCell>
                            {/* <TableCell className="px-4 py-3">
                              <Badge
                                variant="outline"
                                className="!border-[#5B9BD5]/30 !text-[#5B9BD5] text-[10px] px-1.5 py-0.5 rounded-full"
                              >
                                {getCalculationMethodLabel(
                                  rule.calculationMethod
                                )}
                              </Badge>
                            </TableCell> */}
                            {/* <TableCell className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3 text-gray-500" />
                                <span className="text-xs text-gray-700">
                                  {rule.effectiveToDate}
                                </span>
                              </div>
                            </TableCell> */}
                            {/* <TableCell className="px-4 py-3">
                              <Badge
                                className={`
                                  border text-[10px] px-1.5 py-0.5 rounded-full
                                  ${
                                    rule.isActive
                                      ? "!bg-green-100 !text-green-700 !border-green-300"
                                      : "!bg-gray-100 !text-gray-700 !border-gray-300"
                                  }
                                `}
                              >
                                {rule.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </TableCell> */}
                            {canEdit && (
                              <TableCell className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEdit(rule)}
                                    className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                    title="Edit age rule"
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="lg:hidden divide-y divide-gray-200">
                    {accommodationRules.map((rule) => (
                      <div
                        key={rule.id}
                        className="p-4 hover:bg-sky-50/30 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Hotel className="w-4 h-4 text-[#5B9BD5]" />
                              <h3 className="font-medium text-gray-900">
                                {rule.categoryName}
                              </h3>
                            </div>
                            <Badge
                              className={getModuleColor(rule.module)}
                              style={{ fontSize: "10px", padding: "2px 6px" }}
                            >
                              {getModuleIcon(rule.module)}
                              <span className="ml-1">{rule.module}</span>
                            </Badge>
                          </div>
                          {/* <Badge
                            className={`${rule.isActive ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5`}
                          >
                            {rule.isActive ? "Active" : "Inactive"}
                          </Badge> */}
                        </div>

                        <div className="space-y-2 mb-3 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Age Range:</span>
                            <span className="font-medium text-gray-900">
                              {rule.minAge} - {rule.maxAge} years
                            </span>
                          </div>
                          {/* <div className="flex justify-between">
                            <span className="text-gray-600">Calculation:</span>
                            <span className="text-gray-900 text-xs">
                              {getCalculationMethodLabel(rule.calculationMethod)}
                            </span>
                          </div> */}
                          {/* <div className="flex justify-between">
                            <span className="text-gray-600">Effective To:</span>
                            <span className="text-gray-900">
                              {rule.effectiveToDate}
                            </span>
                          </div> */}
                        </div>

                        <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(rule)}
                            className="flex-1 text-[#5B9BD5] border-[#5B9BD5]/30 hover:bg-sky-50 h-8"
                          >
                            <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                            Edit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
                <Pagination
                  totalItems={totalCount}
                  itemsPerPage={pageSize}
                  currentPage={pageNo}
                  onPageChange={(page) => setPageNo(page)}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPageNo(1);
                  }}
                  label="age rules"
                />
              </Card>
            )}

            {/* Transport Rules */}
            {transportRules.length > 0 && (
              <Card
                className="bg-white border-[#5B9BD5]/30"
                style={{ gap: "0px" }}
              >
                <CardContent className="p-0">
                  <div className="hidden lg:block overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                          <SortableHeader
                            column="categoryName"
                            label="Category Name"
                          />
                          <SortableHeader column="minAge" label="Min Age" />
                          <SortableHeader column="maxAge" label="Max Age" />
                          {/* <SortableHeader
                            column="calculationMethod"
                            label="Calculation Method"
                          /> */}
                          {/* <SortableHeader
                            column="effectiveToDate"
                            label="Effective To Date"
                          /> */}
                          {/* <SortableHeader column="isActive" label="Status" /> */}
                          {canEdit && (
                            <TableHead
                              style={{ color: "#5B9BD5" }}
                              className="px-4 py-3 text-xs text-right"
                            >
                              Actions
                            </TableHead>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {transportRules.map((rule) => (
                          <TableRow key={rule.id} className="hover:bg-sky-50/30">
                            <TableCell className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                                  <Users className="w-4 h-4 text-[#5B9BD5]" />
                                </div>
                                <span className="text-gray-900 text-sm">
                                  {rule.categoryName}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="px-4 py-3">
                              <span className="text-xs text-gray-700">
                                {rule.minAge} years
                              </span>
                            </TableCell>
                            <TableCell className="px-4 py-3">
                              <span className="text-xs text-gray-700">
                                {rule.maxAge} years
                              </span>
                            </TableCell>
                            {/* <TableCell className="px-4 py-3">
                              <Badge
                                variant="outline"
                                className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] px-1.5 py-0.5"
                              >
                                {getCalculationMethodLabel(
                                  rule.calculationMethod
                                )}
                              </Badge>
                            </TableCell> */}
                            {/* <TableCell className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3 text-gray-500" />
                                <span className="text-xs text-gray-700">
                                  {rule.effectiveToDate}
                                </span>
                              </div>
                            </TableCell> */}
                            {/* <TableCell className="px-4 py-3">
                              <Badge
                                className={`${rule.isActive ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5`}
                              >
                                {rule.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </TableCell> */}
                            {canEdit && (
                              <TableCell className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEdit(rule)}
                                    className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                    title="Edit age rule"
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile Card View */}
                  <div className="lg:hidden divide-y divide-gray-200">
                    {transportRules.map((rule) => (
                      <div
                        key={rule.id}
                        className="p-4 hover:bg-sky-50/30 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Car className="w-4 h-4 text-[#5B9BD5]" />
                              <h3 className="font-medium text-gray-900">
                                {rule.categoryName}
                              </h3>
                            </div>
                            <Badge
                              className={getModuleColor(rule.module)}
                              style={{ fontSize: "10px", padding: "2px 6px" }}
                            >
                              {getModuleIcon(rule.module)}
                              <span className="ml-1">{rule.module}</span>
                            </Badge>
                          </div>
                          <Badge
                            className={`${rule.isActive ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5`}
                          >
                            {rule.isActive ? "Active" : "Inactive"}
                          </Badge>
                        </div>

                        <div className="space-y-2 mb-3 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Age Range:</span>
                            <span className="font-medium text-gray-900">
                              {rule.minAge} - {rule.maxAge} years
                            </span>
                          </div>
                          {/* <div className="flex justify-between">
                            <span className="text-gray-600">Calculation:</span>
                            <span className="text-gray-900 text-xs">
                              {getCalculationMethodLabel(rule.calculationMethod)}
                            </span>
                          </div> */}
                          {/* <div className="flex justify-between">
                            <span className="text-gray-600">Effective To:</span>
                            <span className="text-gray-900">
                              {rule.effectiveToDate}
                            </span>
                          </div> */}
                        </div>

                        <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(rule)}
                            className="flex-1 text-[#5B9BD5] border-[#5B9BD5]/30 hover:bg-sky-50 h-8"
                          >
                            <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                            Edit
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          <TabsContent value="accommodation">
            <Card className="bg-white border-[#5B9BD5]/30" style={{ gap: "0px" }}>
              <CardContent style={{ padding: "0px" }}>
                <div className="hidden lg:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                        <SortableHeader
                          column="categoryName"
                          label="Category Name"
                        />
                        <SortableHeader column="minAge" label="Min Age" />
                        <SortableHeader column="maxAge" label="Max Age" />
                        {/* <SortableHeader
                          column="calculationMethod"
                          label="Calculation Method"
                        /> */}
                        {/* <SortableHeader
                          column="effectiveToDate"
                          label="Effective To Date"
                        /> */}
                        {/* <SortableHeader column="isActive" label="Status" /> */}
                        {canEdit && (
                          <TableHead
                            style={{ color: "#5B9BD5" }}
                            className="px-4 py-3 text-xs text-right"
                          >
                            Actions
                          </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {accommodationRules.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            className="text-center py-8 px-4 text-gray-500 text-sm"
                          >
                            No accommodation age rules found
                          </TableCell>
                        </TableRow>
                      ) : (
                        accommodationRules.map((rule) => (
                          <TableRow
                            key={rule.id}
                            className="bg-white hover:bg-sky-50/30"
                          >
                            <TableCell className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                                  <Users className="w-4 h-4 text-[#5B9BD5]" />
                                </div>
                                <span className="text-gray-900 text-sm">
                                  {rule.categoryName}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="px-4 py-3">
                              <span className="text-xs text-gray-700">
                                {rule.minAge} years
                              </span>
                            </TableCell>
                            <TableCell className="px-4 py-3">
                              <span className="text-xs text-gray-700">
                                {rule.maxAge} years
                              </span>
                            </TableCell>
                            {/* <TableCell className="px-4 py-3">
                              <Badge
                                variant="outline"
                                className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] px-1.5 py-0.5"
                              >
                                {getCalculationMethodLabel(
                                  rule.calculationMethod
                                )}
                              </Badge>
                            </TableCell> */}
                            {/* <TableCell className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3 text-gray-500" />
                                <span className="text-xs text-gray-700">
                                  {rule.effectiveToDate}
                                </span>
                              </div>
                            </TableCell> */}
                            {/* <TableCell className="px-4 py-3">
                              <Badge
                                className={`${rule.isActive ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5`}
                              >
                                {rule.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </TableCell> */}
                            {canEdit && (
                              <TableCell className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEdit(rule)}
                                    className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                    title="Edit age rule"
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </Button>
                                  {/* <Button
                                    size="sm"
                                    variant="ghost"
                                    // onClick={() => handleDelete(rule.id)}
                                    className="text-red-600 hover:bg-red-50 h-7 w-7 p-0"
                                    title="Delete age rule"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button> */}
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile Card View */}
                <div className="lg:hidden divide-y divide-gray-200">
                  {accommodationRules.length === 0 ? (
                    <div className="text-center py-8 px-4 text-gray-500 text-sm">
                      No accommodation age rules found
                    </div>
                  ) : (
                    accommodationRules.map((rule) => (
                      <div
                        key={rule.id}
                        className="p-4 hover:bg-sky-50/30 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Hotel className="w-4 h-4 text-[#5B9BD5]" />
                              <h3 className="font-medium text-gray-900">
                                {rule.categoryName}
                              </h3>
                            </div>
                          </div>
                          {/* <Badge
                            className={`${rule.isActive ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5`}
                          >
                            {rule.isActive ? "Active" : "Inactive"}
                          </Badge> */}
                        </div>

                        <div className="space-y-2 mb-3 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Age Range:</span>
                            <span className="font-medium text-gray-900">
                              {rule.minAge} - {rule.maxAge} years
                            </span>
                          </div>
                          {/* <div className="flex justify-between">
                            <span className="text-gray-600">Calculation:</span>
                            <span className="text-gray-900 text-xs">
                              {getCalculationMethodLabel(rule.calculationMethod)}
                            </span>
                          </div> */}
                          {/* <div className="flex justify-between">
                            <span className="text-gray-600">Effective To:</span>
                            <span className="text-gray-900">
                              {rule.effectiveToDate}
                            </span>
                          </div> */}
                        </div>

                        <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(rule)}
                            className="flex-1 text-[#5B9BD5] border-[#5B9BD5]/30 hover:bg-sky-50 h-8"
                          >
                            <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                            Edit
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
                <Pagination
                  totalItems={totalCount}
                  itemsPerPage={pageSize}
                  currentPage={pageNo}
                  onPageChange={(page) => setPageNo(page)}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPageNo(1);
                  }}
                  label="age rules"
                />
            </Card>
          </TabsContent>

          <TabsContent value="transport">
            <Card className="bg-white border-[#5B9BD5]/30" style={{ gap: "0px" }}>
              <CardContent style={{ padding: "0px" }}>
                <div className="hidden lg:block overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow style={{ backgroundColor: "#E3F2FD" }}>
                        <SortableHeader
                          column="categoryName"
                          label="Category Name"
                        />
                        <SortableHeader column="minAge" label="Min Age" />
                        <SortableHeader column="maxAge" label="Max Age" />
                        {/* <SortableHeader
                          column="calculationMethod"
                          label="Calculation Method"
                        /> */}
                        {/* <SortableHeader
                          column="effectiveToDate"
                          label="Effective To Date"
                        /> */}
                        {/* <SortableHeader column="isActive" label="Status" /> */}
                        {canEdit && (
                          <TableHead
                            style={{ color: "#5B9BD5" }}
                            className="px-4 py-3 text-xs text-right"
                          >
                            Actions
                          </TableHead>
                        )}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {transportRules.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={7}
                            className="text-center py-8 px-4 text-gray-500 text-sm"
                          >
                            No transport age rules found
                          </TableCell>
                        </TableRow>
                      ) : (
                        transportRules.map((rule) => (
                          <TableRow key={rule.id} className="hover:bg-sky-50/30">
                            <TableCell className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-sky-100 to-blue-100 flex items-center justify-center flex-shrink-0">
                                  <Users className="w-4 h-4 text-[#5B9BD5]" />
                                </div>
                                <span className="text-gray-900 text-sm">
                                  {rule.categoryName}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="px-4 py-3">
                              <span className="text-xs text-gray-700">
                                {rule.minAge} years
                              </span>
                            </TableCell>
                            <TableCell className="px-4 py-3">
                              <span className="text-xs text-gray-700">
                                {rule.maxAge} years
                              </span>
                            </TableCell>
                            {/* <TableCell className="px-4 py-3">
                              <Badge
                                variant="outline"
                                className="border-[#5B9BD5]/30 text-[#5B9BD5] text-[10px] px-1.5 py-0.5"
                              >
                                {getCalculationMethodLabel(
                                  rule.calculationMethod
                                )}
                              </Badge>
                            </TableCell> */}
                            {/* <TableCell className="px-4 py-3">
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3 text-gray-500" />
                                <span className="text-xs text-gray-700">
                                  {rule.effectiveToDate}
                                </span>
                              </div>
                            </TableCell> */}
                            {/* <TableCell className="px-4 py-3">
                              <Badge
                                className={`${rule.isActive ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5`}
                              >
                                {rule.isActive ? "Active" : "Inactive"}
                              </Badge>
                            </TableCell> */}
                            {canEdit && (
                              <TableCell className="px-4 py-3 text-right">
                                <div className="flex items-center justify-end gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => handleEdit(rule)}
                                    className="text-[#5B9BD5] hover:bg-sky-50 h-7 w-7 p-0"
                                    title="Edit age rule"
                                  >
                                    <Edit2 className="w-3.5 h-3.5" />
                                  </Button>
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* Mobile Card View */}
                <div className="lg:hidden divide-y divide-gray-200">
                  {transportRules.length === 0 ? (
                    <div className="text-center py-8 px-4 text-gray-500 text-sm">
                      No transport age rules found
                    </div>
                  ) : (
                    transportRules.map((rule) => (
                      <div
                        key={rule.id}
                        className="p-4 hover:bg-sky-50/30 transition-colors"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <Car className="w-4 h-4 text-[#5B9BD5]" />
                              <h3 className="font-medium text-gray-900">
                                {rule.categoryName}
                              </h3>
                            </div>
                          </div>
                          {/* <Badge
                            className={`${rule.isActive ? "bg-green-100 text-green-700 border-green-300" : "bg-gray-100 text-gray-700 border-gray-300"} text-[10px] px-1.5 py-0.5`}
                          >
                            {rule.isActive ? "Active" : "Inactive"}
                          </Badge> */}
                        </div>

                        <div className="space-y-2 mb-3 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Age Range:</span>
                            <span className="font-medium text-gray-900">
                              {rule.minAge} - {rule.maxAge} years
                            </span>
                          </div>
                          {/* <div className="flex justify-between">
                            <span className="text-gray-600">Calculation:</span>
                            <span className="text-gray-900 text-xs">
                              {getCalculationMethodLabel(rule.calculationMethod)}
                            </span>
                          </div> */}
                          {/* <div className="flex justify-between">
                            <span className="text-gray-600">Effective To:</span>
                            <span className="text-gray-900">
                              {rule.effectiveToDate}
                            </span>
                          </div> */}
                        </div>

                        <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(rule)}
                            className="flex-1 text-[#5B9BD5] border-[#5B9BD5]/30 hover:bg-sky-50 h-8"
                          >
                            <Edit2 className="w-3.5 h-3.5 mr-1.5" />
                            Edit
                          </Button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
                <Pagination
                  totalItems={totalCount}
                  itemsPerPage={pageSize}
                  currentPage={pageNo}
                  onPageChange={(page) => setPageNo(page)}
                  onPageSizeChange={(size) => {
                    setPageSize(size);
                    setPageNo(1);
                  }}
                  label="age rules"
                />
            </Card>
          </TabsContent>
        </Tabs>

        {/* Add/Edit Dialog */}
        <Dialog open={showDialog} onOpenChange={handleCloseDialog}>
          <DialogContent className="bg-white max-w-md max-h-[90vh] overflow-y-auto w-[calc(100vw-2rem)] sm:w-full mx-4 sm:mx-auto">
            <DialogHeader>
              <DialogTitle
                style={{ color: "#5B9BD5" }}
                className="text-base sm:text-lg"
              >
                {editingRule ? "Edit Age Rule" : "Add Age Rule"}
              </DialogTitle>
              <DialogDescription className="text-xs sm:text-sm">
                {editingRule
                  ? "Update age rule details"
                  : "Define a new age category for a specific module"}
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3 sm:space-y-4 py-3 ">
              <div className="space-y-2">
                <InputFieldset
                  label="Category Name"
                  required
                  id="categoryName"
                  placeholder="e.g., Infant, Child, Adult"
                  value={formData.categoryName}
                  maxLength={15}
                  onChange={(e) => {
                    const value = e.target.value;

                    // Allow only letters and spaces, max length 15
                    if (/^[A-Za-z\s]*$/.test(value) && value.length <= 15) {
                      setFormData({
                        ...formData,
                        categoryName: value,
                      });
                    }
                  }}
                  className="text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <InputFieldset
                    label="Minimum Age"
                    required
                    id="minAge"
                    type="number"
                    min="0"
                    placeholder="0"
                    value={formData.minAge}
                    onChange={(e) =>
                      setFormData({ ...formData, minAge: e.target.value })
                    }
                    className="text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <InputFieldset
                    label="Maximum Age"
                    required
                    id="maxAge"
                    type="number"
                    min="0"
                    placeholder="120"
                    value={formData.maxAge}
                    onChange={(e) =>
                      setFormData({ ...formData, maxAge: e.target.value })
                    }
                    className="text-sm"
                  />
                </div>
              </div>

              {/* <div className="space-y-2">
                <Label htmlFor="module" className="text-sm">
                  Module <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={formData.module}
                  onValueChange={(value) =>
                    setFormData({ ...formData, module: value })
                  }
                >
                  <SelectTrigger id="module" className="text-sm">
                    <SelectValue placeholder="Select module" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="visa">
                      <div className="flex items-center gap-2">
                        <FileText className="w-4 h-4" />
                        Visa
                      </div>
                    </SelectItem>
                    <SelectItem value="accommodation">
                      <div className="flex items-center gap-2">
                        <Hotel className="w-4 h-4" />
                        Accommodation
                      </div>
                    </SelectItem>
                    <SelectItem value="transport">
                      <div className="flex items-center gap-2">
                        <Car className="w-4 h-4" />
                        Transport
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div> */}

              <div className="space-y-2">
                {/* <SelectFieldset label="Calculation Method" required>
                  <Select
                    value={formData.calculationMethod}
                    onValueChange={(value) =>
                      setFormData({ ...formData, calculationMethod: value })
                    }
                  >
                    <SelectTriggerUI id="calculationMethod" className="text-sm">
                      <SelectValue placeholder="Select calculation method" />
                    </SelectTriggerUI>
                    <SelectContent>
                      {calculationMethods.map((method) => (
                        <SelectItem key={method.value} value={method.value}>
                          <div className="flex flex-col items-start" style={{ padding: "10px" }}>
                            <span className="font-medium">{method.label}</span>
                            <span className="text-xs text-gray-500">
                              {method.description}
                            </span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectFieldset> */}
                {/* {formData.calculationMethod && (
                  <Alert className="mt-2">
                    <Info className="w-4 h-4" />
                    <AlertDescription className="text-xs">
                      {
                        calculationMethods.find(
                          (m) => m.value === formData.calculationMethod
                        )?.description
                      }
                    </AlertDescription>
                  </Alert>
                )} */}
              </div>

              {/* <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <InputFieldset
                    label="Effective From Date"
                    required
                    id="effectiveFromDate"
                    type="date"
                    value={formData.effectiveFromDate}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        effectiveFromDate: e.target.value,
                      })
                    }
                    className="text-sm"
                  />
                </div>

                <div className="space-y-2">
                  <InputFieldset
                    label="Effective To Date"
                    required
                    id="effectiveToDate"
                    type="date"
                    value={formData.effectiveToDate}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        effectiveToDate: e.target.value,
                      })
                    }
                    className="text-sm"
                  />
                </div>
              </div> */}

              <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                <Label htmlFor="isActive" className="text-sm cursor-pointer">
                  Active Status
                </Label>
                <Switch
                  id="isActive"
                  checked={formData.isActive}
                  onCheckedChange={(checked) =>
                    setFormData({ ...formData, isActive: checked })
                  }
                />
              </div>
            </div>

            <DialogFooter className="flex-col sm:flex-row gap-4">
              <Button
                variant="outline"
                onClick={handleCloseDialog}
                className="text-sm w-full sm:w-auto"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                style={{ background: "#5B9BD5" }}
                className="text-white text-sm w-full sm:w-auto"
              >
                {editingRule ? "Update" : "Add"} Age Rule
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      
        
      </div>
    </>
  );
};
